import IconAdd from "@shared/components/icons/Add"
import IconBin from "@shared/components/icons/Bin"
import IconEllipsis from "@shared/components/icons/Ellipsis"
import {
  DrilldownMenu,
  DrilldownRow,
  HeaderWithBackArrow,
  MenuHeader,
  MenuRoot,
  MenuTitle,
  RowContent,
  RowIcon,
  RowLabel,
  Rows,
} from "@shared/components/menus/DrilldownMenu"
import { FLAT_MENU_ROWS_CLASS } from "@shared/components/menus/drilldown-menu-classes"
import { ConfirmView } from "@shared/components/menus/views/ConfirmView"
import { cx } from "@shared/components/untitledui/util/cx"
import { AddButton } from "@shared/unified-tiptap/editor-ui/add-button/AddButton"
import { CardPillButton } from "@shared/unified-tiptap/editor-ui/card-buttons/CardPillButton"
import { PLACEHOLDERS, placeholderStyle } from "@shared/unified-tiptap/placeholders"
import { useUiContext } from "@shared/unified-tiptap/unified-tiptap-ui-context"
import { useHoverChrome } from "@shared/unified-tiptap/use-hover-chrome"
import { findAddTargetPos } from "@shared/unified-tiptap/util/find-add-target-pos"
import { tracked } from "@supergrain/kernel/react"
import type { ExtensionDateAnnotationNoteAttrs } from "@tiptap-content/extensions/date-annotation-note/date-annotation-note-content-extension"
import { NodeViewContent, NodeViewWrapper, type ReactNodeViewProps } from "@tiptap/react"
import { useState } from "react"

// A long word breaks at the note's edge instead of overflowing; hyphens-auto upgrades that to
// a real hyphen where supported. min-w keeps room for the "Title" prompt when empty.
const NOTE_TITLE =
  "mb-1.5 min-h-[1.4rem] min-w-[50px] text-[1.4rem] leading-[1.15] font-bold tracking-[-0.2px] hyphens-auto"

export const DateAnnotationNoteTitleComponent = () => {
  const { notePrompts } = useUiContext()
  return (
    <NodeViewWrapper
      className="select-none"
      data-content-type="dateAnnotationNoteTitle"
      data-test-course-annotation-title=""
    >
      <NodeViewContent
        className={NOTE_TITLE}
        style={{
          ...placeholderStyle(notePrompts?.title ?? PLACEHOLDERS.cardTitle),
        }}
      />
    </NodeViewWrapper>
  )
}

export const DateAnnotationNoteComponent = tracked((props: ReactNodeViewProps) => {
  const attrs = props.node.attrs as ExtensionDateAnnotationNoteAttrs
  const { permissions, onDeleteNote, setCardOverlayOpen, hideButtons, notePrompts } = useUiContext()
  const { chromeVisible, chromeProps } = useHoverChrome("cardButtons")

  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const setAnyOverlayOpen = (next: { addOpen?: boolean; menuOpen?: boolean }) => {
    const addOpen = next.addOpen ?? isAddOpen
    const menuOpen = next.menuOpen ?? isMenuOpen
    setCardOverlayOpen(addOpen || menuOpen)
  }

  const setAddOpen = (isOpen: boolean) => {
    setIsAddOpen(isOpen)
    setAnyOverlayOpen({ addOpen: isOpen })
  }

  const setMenuOpen = (isOpen: boolean) => {
    setIsMenuOpen(isOpen)
    setAnyOverlayOpen({ menuOpen: isOpen })
  }

  const getAddTargetPos = (): number | null => {
    const notePos = props.getPos()
    if (notePos === undefined) return null

    const { head } = props.editor.state.selection
    if (head > notePos && head < notePos + props.node.nodeSize) {
      return findAddTargetPos(props.editor, head).pos
    }
    return notePos + 1
  }

  return (
    <NodeViewWrapper
      // The note takes its ground from the class's color, so the empty-slot prompts need to be
      // darker than the default gray to stay legible on it (see --placeholder-color in
      // unified-tiptap.css).
      className={cx(
        "relative flex flex-col bg-transparent px-2.5 pt-3 pb-4 text-[#1a2b2e] select-none",
        "[--placeholder-color:rgb(0_0_0/0.4)]",
      )}
      data-content-type="dateAnnotationNote"
      {...chromeProps}
    >
      {permissions.canEdit && !hideButtons && (
        <div
          className={cx(
            "absolute top-2 right-2.5 z-10 flex flex-row items-center transition-opacity duration-150 ease-in-out",
            "card-pill-shadow",
            chromeVisible || isAddOpen || isMenuOpen
              ? "opacity-100"
              : "pointer-events-none opacity-0 focus-within:pointer-events-auto focus-within:opacity-100",
          )}
          contentEditable={false}
        >
          <NoteMenu onDelete={onDeleteNote} onOpenChange={setMenuOpen}>
            <CardPillButton
              ariaLabel="Note menu"
              label="Menu"
              icon={<IconEllipsis />}
              isMenuOpen={isMenuOpen}
              side="left"
              attachedSide="right"
              dataTest="note-menu-button"
            />
          </NoteMenu>

          <AddButton
            editor={props.editor}
            mode="dateAnnotationNote"
            getAddTargetPos={getAddTargetPos}
            getCardId={() => attrs.id}
            onOpenChange={setAddOpen}
          >
            <CardPillButton
              ariaLabel="Add to note"
              label="Add"
              icon={<IconAdd />}
              isMenuOpen={isAddOpen}
              attachedSide="left"
            />
          </AddButton>
        </div>
      )}

      <NodeViewContent
        className="unified-tiptap-card"
        data-id={attrs.id}
        style={placeholderStyle(notePrompts?.description ?? PLACEHOLDERS.emptyCard)}
      />
    </NodeViewWrapper>
  )
})

function NoteMenu({
  children,
  onDelete,
  onOpenChange,
}: {
  children: React.ReactNode
  onDelete?: () => void
  onOpenChange?: (isOpen: boolean) => void
}) {
  return (
    <DrilldownMenu
      ariaLabel="Note menu"
      className="w-72"
      trigger={children}
      header={
        <MenuHeader className="border-b border-border-secondary">
          <MenuTitle>Note Menu</MenuTitle>
        </MenuHeader>
      }
      onOpenChange={onOpenChange}
    >
      {({ close }) => (
        <MenuRoot>
          <Rows className={FLAT_MENU_ROWS_CLASS}>
            <DrilldownRow
              typeaheadText="Delete Note"
              header={({ back }) => (
                <HeaderWithBackArrow backTo="Note Menu" onBack={back}>
                  Delete Note
                </HeaderWithBackArrow>
              )}
              body={({ back }) => (
                <ConfirmView
                  body="Deleting this note will also hide the class."
                  confirmLabel="Delete"
                  destructive
                  onCancel={back}
                  onConfirm={() => {
                    close()
                    onDelete?.()
                  }}
                />
              )}
            >
              <RowContent data-test="delete-note-row">
                <RowIcon icon={IconBin} />
                <RowLabel>Delete Note</RowLabel>
              </RowContent>
            </DrilldownRow>
          </Rows>
        </MenuRoot>
      )}
    </DrilldownMenu>
  )
}
